import "@arcgis/map-components/components/arcgis-feature-table";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer.js";
import { findLayerOrSublayer } from '../../utils/mapUtils.js';
import { initLayerPopup } from "@libs/utils/mapUtils.js";

const VISIBLE_FIELDS = ["API10","CURRENT_STATUS","COMPLETION_DATE","FIELD_NAME","OPERATOR_NAME","LEASE_NAME","ReservationName"];//,"WELL_ID","ID"]; 

//"CURRENT_COMPL",,"WELL_ID"

export const createFeatureTable = async (mapView, layerTitle, containerId, options = {}) => {
  await mapView.when();
  const targetLayer = findLayerOrSublayer(mapView.map, layerTitle);

  if (!targetLayer) return { error: "Layer not found" };

  await initLayerPopup(targetLayer);

  // Highlight layer, same as before
  const highlightLayer = new GraphicsLayer({
    id: `highlight-layer-${containerId}`,
    listMode: "hide"
  });
  mapView.map.add(highlightLayer);

  // Build column templates (same logic as before)
  const columnTemplates = VISIBLE_FIELDS.map(field => {
    const config = {
      type: "field",
      fieldName: field,
      label: field.replace(/_/g, " ") // e.g. "API_NUMBER" -> "API NUMBER"
    };

    if (field === "COMPLETION_DATE") {
      config.format = {
        dateFormat: "short-date" // Forces MM/DD/YYYY, avoids 2-digit year issues
      };
    }

    return config;
  });

  // Create the web component element
  const table = document.createElement("arcgis-feature-table");

  // Since mapView was created imperatively (not via <arcgis-map>),
  // set the view directly instead of using a referenceElement attribute.
  table.view = mapView;
  table.layer = targetLayer;

  table.filterBySelectionEnabled = options.filterBySelectionEnabled || false;
  table.highlightEnabled = true;
  table.pageSize = 16;

  table.hiddenFields = targetLayer.fields
    .map(f => f.name)
    .filter(name => !VISIBLE_FIELDS.includes(name));

  table.tableTemplate = { columnTemplates };

  // Mount into the target container
  const container = document.getElementById(containerId);
  if (!container) return { error: `Container "${containerId}" not found` };

  container.innerHTML = "";
  container.appendChild(table);

  return { table, highlightLayer };
};

/*
Fields:
OBJECTID ( type: esriFieldTypeOID, alias: OBJECTID )
API_NUMBER ( type: esriFieldTypeString, alias: API_NUMBER, length: 15 )
COMPLETION_DATE ( type: esriFieldTypeDateOnly, alias: COMPLETION_DATE )
COMPLETION_SEQ ( type: esriFieldTypeSmallInteger, alias: COMPLETION_SEQ )
CUM_DAYS_ON_INJECTION ( type: esriFieldTypeSmallInteger, alias: CUM_DAYS_ON_INJECTION )
CUM_DAYS_ON_PRODUCTION ( type: esriFieldTypeSmallInteger, alias: CUM_DAYS_ON_PRODUCTION )
CUM_GAS ( type: esriFieldTypeInteger, alias: CUM_GAS )
CUM_GASOIL_RATIO ( type: esriFieldTypeDouble, alias: CUM_GASOIL_RATIO )
CUM_INJECTION_DATA ( type: esriFieldTypeString, alias: CUM_INJECTION_DATA, length: 3 )
CUM_INJECTION_GAS ( type: esriFieldTypeInteger, alias: CUM_INJECTION_GAS )
CUM_INJECTION_OIL ( type: esriFieldTypeInteger, alias: CUM_INJECTION_OIL )
CUM_INJECTION_WATER ( type: esriFieldTypeInteger, alias: CUM_INJECTION_WATER )
CUM_OIL ( type: esriFieldTypeInteger, alias: CUM_OIL )
CUM_WATER ( type: esriFieldTypeInteger, alias: CUM_WATER )
CURRENT_COMPL ( type: esriFieldTypeString, alias: CURRENT_COMPL, length: 1 )
CURRENT_STATUS ( type: esriFieldTypeString, alias: CURRENT_STATUS, length: 7 )
DATA_SOURCE ( type: esriFieldTypeString, alias: DATA_SOURCE, length: 10 )
DRILL_TOTAL_DEPTH ( type: esriFieldTypeInteger, alias: DRILL_TOTAL_DEPTH )
FIELD_NAME ( type: esriFieldTypeString, alias: FIELD_NAME, length: 40 )
FINAL_LAHEE_CLASS ( type: esriFieldTypeString, alias: FINAL_LAHEE_CLASS, length: 3 )
FINAL_WELL_TYPE ( type: esriFieldTypeString, alias: FINAL_WELL_TYPE, length: 20 )
FIRST_CURRENT_COMPL ( type: esriFieldTypeString, alias: FIRST_CURRENT_COMPL, length: 1 )
FIRST_DAYS_ON_INJECTION ( type: esriFieldTypeSmallInteger, alias: FIRST_DAYS_ON_INJECTION )
FIRST_DAYS_ON_PRODUCTION ( type: esriFieldTypeSmallInteger, alias: FIRST_DAYS_ON_PRODUCTION )
FIRST_GAS ( type: esriFieldTypeInteger, alias: FIRST_GAS )
FIRST_GASOIL_RATIO ( type: esriFieldTypeDouble, alias: FIRST_GASOIL_RATIO )
FIRST_INJECTION_GAS ( type: esriFieldTypeInteger, alias: FIRST_INJECTION_GAS )
FIRST_INJECTION_OIL ( type: esriFieldTypeInteger, alias: FIRST_INJECTION_OIL )
FIRST_INJECTION_WATER ( type: esriFieldTypeInteger, alias: FIRST_INJECTION_WATER )
FIRST_OIL ( type: esriFieldTypeInteger, alias: FIRST_OIL )
FIRST_PRODUCTION_DATE ( type: esriFieldTypeDateOnly, alias: FIRST_PRODUCTION_DATE )
FIRST_WATER ( type: esriFieldTypeInteger, alias: FIRST_WATER )
FORMATION_AT_TD ( type: esriFieldTypeString, alias: FORMATION_AT_TD, length: 40 )
GAS_INJ_OPENING_CUM ( type: esriFieldTypeInteger, alias: GAS_INJ_OPENING_CUM )
GAS_OPENING_CUM ( type: esriFieldTypeInteger, alias: GAS_OPENING_CUM )
LAST_DAYS_ON_INJECTION ( type: esriFieldTypeSmallInteger, alias: LAST_DAYS_ON_INJECTION )
LAST_DAYS_ON_PRODUCTION ( type: esriFieldTypeSmallInteger, alias: LAST_DAYS_ON_PRODUCTION )
LAST_GAS ( type: esriFieldTypeInteger, alias: LAST_GAS )
LAST_GASOIL_RATIO ( type: esriFieldTypeDouble, alias: LAST_GASOIL_RATIO )
LAST_INJECTION_GAS ( type: esriFieldTypeInteger, alias: LAST_INJECTION_GAS )
LAST_INJECTION_OIL ( type: esriFieldTypeInteger, alias: LAST_INJECTION_OIL )
LAST_INJECTION_WATER ( type: esriFieldTypeInteger, alias: LAST_INJECTION_WATER )
LAST_OIL ( type: esriFieldTypeInteger, alias: LAST_OIL )
LAST_PRODUCTION_DATE ( type: esriFieldTypeDateOnly, alias: LAST_PRODUCTION_DATE )
LAST_WATER ( type: esriFieldTypeInteger, alias: LAST_WATER )
LATITUDE_BH ( type: esriFieldTypeDouble, alias: LATITUDE_BH )
LEASE_NAME ( type: esriFieldTypeString, alias: LEASE_NAME, length: 36 )
LONGITUDE_BH ( type: esriFieldTypeDouble, alias: LONGITUDE_BH )
LOWER_PERFORATION ( type: esriFieldTypeInteger, alias: LOWER_PERFORATION )
OIL_INJ_OPENING_CUM ( type: esriFieldTypeInteger, alias: OIL_INJ_OPENING_CUM )
OIL_OPENING_CUM ( type: esriFieldTypeInteger, alias: OIL_OPENING_CUM )
OPERATOR_NAME ( type: esriFieldTypeString, alias: OPERATOR_NAME, length: 36 )
PROD_ID ( type: esriFieldTypeString, alias: PROD_ID, length: 40 )
PRODUCING_ZONE ( type: esriFieldTypeString, alias: PRODUCING_ZONE, length: 60 )
UPPER_PERFORATION ( type: esriFieldTypeInteger, alias: UPPER_PERFORATION )
WATER_INJ_OPENING_CUM ( type: esriFieldTypeInteger, alias: WATER_INJ_OPENING_CUM )
WATER_OPENING_CUM ( type: esriFieldTypeInteger, alias: WATER_OPENING_CUM )
WELL_NUMBER ( type: esriFieldTypeString, alias: WELL_NUMBER, length: 20 )
RESERVATION_ID ( type: esriFieldTypeInteger, alias: RESERVATION_ID )
SPUD_DATE ( type: esriFieldTypeDateOnly, alias: SPUD_DATE )
DELETED ( type: esriFieldTypeSmallInteger, alias: Deleted )
WELL_ID ( type: esriFieldTypeGUID, alias: WELL_ID, length: 38 )
WELL_COMPL_ID ( type: esriFieldTypeGUID, alias: WELL_COMPL_ID, length: 38 )
WELL_PS_ID ( type: esriFieldTypeGUID, alias: WELL_PS_ID, length: 38 )
LATITUDE ( type: esriFieldTypeDouble, alias: LATITUDE )
LONGITUDE ( type: esriFieldTypeDouble, alias: LONGITUDE )
DATE_CREATED_WD ( type: esriFieldTypeDateOnly, alias: Date created WriteDate )
DATE_UPDATED_WD ( type: esriFieldTypeDateOnly, alias: Date updated WriteDate )
DATE_DELETED_WD ( type: esriFieldTypeDateOnly, alias: Date deleted WriteDate )
ELEVATION ( type: esriFieldTypeInteger, alias: ELEVATION )
DATE_CREATED ( type: esriFieldTypeDate, alias: DATE_CREATED, length: 8 )
DATE_UPDATED ( type: esriFieldTypeDate, alias: DATE_UPDATED, length: 8 )
DATE_DELETED ( type: esriFieldTypeDate, alias: DATE_DELETED, length: 8 )
Shape ( type: esriFieldTypeGeometry, alias: SHAPE )
ID ( type: esriFieldTypeGUID, alias: ID, length: 38 )
*/